import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { apiService } from '../services/api';
import { useAppStore } from '../store/appStore';
import {
  InnoCallsRTCInstance,
  ensureInnocallsStylesheet,
  ensureInnocallsScript,
  waitForInnocallsRTC,
  destroyInnoCallsInstance,
  normalizePhoneNumber,
  formatDialTarget,
  PhoneDialerEndReason,
  subscribePeerConnectionDiagnostics,
  subscribeSipSignals,
} from '../services/phone-dialer.service';
import { teardownNoiseSuppression, releaseActiveMicStreams } from '../services/noise-suppression.service';

const DIALER_ROOT_ID = 'innocalls-dialer-root';

// How long a call can stay in the "ringing" state before we assume it must
// have actually been answered (see armRingingTimeout's doc comment — this
// SDK build has no reliable answered event, so genuinely answered calls look
// identical to still-ringing ones). Comfortably longer than any real ringing
// period a human would let run before either picking up or the call
// resolving via a real SDK event (missed/rejected/ended).
const RINGING_ASSUME_ANSWERED_MS = 45000;

// Based on InnoCalls' SDK outcomes, but onCallEnded alone isn't reliable
// evidence of a genuinely completed call (confirmed: a call declined within
// ~9s fired onCallEnded, not onCallRejected) — so 'completed' additionally
// requires having actually reached a genuine answered state; an onCallEnded
// that arrives before that is treated as 'cancelled' instead. See
// finalizeCall for the exact logic. 'not-responded' = onCallMissed.
// 'cancelled' = onCallRejected, or onCallEnded before ever answering.
// 'ended' = OUR app's own signal: the agent pressed "End Call" before any
// SDK outcome arrived.
export type PhoneDialerCallState = 'idle' | 'calling' | 'ringing' | 'connected' | 'completed' | 'not-responded' | 'cancelled' | 'ended' | 'failed';

export interface PhoneCallContactInfo {
  name: string;
  avatar?: string;
  /** Channels this contact is reachable on (e.g. 'whatsapp', 'facebook-messenger',
   * 'email') — rendered as small identifier icons on the in-call screen. */
  linkedChannels?: string[];
  /** Omni-contact (parent contact) id — used to load/add notes and to open
   * the right conversation when a channel icon is tapped. */
  contactId?: string;
  /** The number that was dialed — kept so a busy/no-answer/failed outcome
   * can offer a one-tap "Call back" without the caller having to re-navigate
   * to the contact. */
  phoneNumber?: string;
  /** The EXACT string handed to InnoCalls' SDK (instance.startCall(dialTarget)) —
   * distinct from phoneNumber above, which is the plain E.164 value BEFORE
   * formatDialTarget's same-country local-format rewrite. Surfaced on the
   * in-call screen so a mismatch between the intended number and what's
   * actually on the wire is visible immediately, rather than only inferable
   * from console logs. */
  dialTarget?: string;
}

interface ActiveCall {
  providerCallId: string;
  phoneNumber: string;
  contactId?: string;
  startedAt: number;
}

/**
 * Drives InnoCalls' WebRTC dialer headlessly — the SDK's own widget is never
 * shown (mounted into an off-screen div, see usePhoneDialerHost below);
 * callers only see this hook's state (idle/ringing/connected/ended) and
 * duration, matching the "no InnoCalls visualization" requirement.
 *
 * Web only for now — InnoCalls has no confirmed native SIP/WebRTC connection
 * details yet (see project_innocalls_api memory); native support is a
 * follow-up once those are obtained from InnoCalls support.
 *
 * @param authReady Defaults to true, correct for every normal in-app screen —
 * ApiService's own constructor restores a persisted auth token from
 * AsyncStorage synchronously before ANY screen (including one calling this
 * hook) ever mounts, so a token is always already present here. CallScreen.tsx
 * is the one exception: it's a standalone, pre-auth page that only gets a
 * real token AFTER an async code-redemption fetch resolves post-mount — this
 * hook's own init effect (which fetches agent credentials, an authenticated
 * call) used to fire immediately on mount regardless, always losing that
 * race, throwing on the missing auth header, and permanently setting
 * isReady=false with no retry — the exact confirmed cause of a real-looking
 * call screen whose contact/name info never actually loaded (isReady never
 * true -> startCall() never called -> phoneDialer.contact stayed null
 * forever). CallScreen.tsx passes its own authReady state through here so
 * this effect waits for, and re-runs once, a real token actually exists.
 */
export function usePhoneDialer(authReady: boolean = true) {
  const isSupportedPlatform = Platform.OS === 'web';

  const [isReady, setIsReady] = useState(false);
  const [callState, setCallState] = useState<PhoneDialerCallState>('idle');
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [contact, setContact] = useState<PhoneCallContactInfo | null>(null);
  const [minimized, setMinimized] = useState(false);
  // TEMPORARY DIAGNOSTIC AID — see subscribePeerConnectionDiagnostics's doc
  // comment in phone-dialer.service.ts. Rolling log of real ICE/connection
  // state + inbound-audio packet stats, rendered on-screen so a real device
  // test call shows exactly what happens at the ~10-12s mark. Remove once
  // the root cause of the audio-drop/call-teardown bug is found and fixed.
  const [diagnostics, setDiagnostics] = useState<string[]>([]);

  useEffect(() => {
    if (!isSupportedPlatform) return;
    return subscribePeerConnectionDiagnostics((event) => {
      setDiagnostics((prev) => [...prev.slice(-49), event.message]);
    });
  }, [isSupportedPlatform]);

  // The call's real SIP outcome (see subscribeSipSignals). Responses to INVITE carry ringing,
  // answered, busy/declined and no-answer; an incoming BYE is the other side hanging up.
  useEffect(() => {
    if (!isSupportedPlatform) return;
    return subscribeSipSignals((signal) => {
      if (!activeCallRef.current || finalizedRef.current) return;
      if (signal.kind === 'request') {
        if (signal.method === 'BYE') finalizeCallRef.current('callEnded');
        return;
      }
      if (signal.method !== 'INVITE' || signal.status < 180) return;
      const status = signal.status;
      console.log('[usePhoneDialer] SIP INVITE response:', status);
      // 401/407 are digest-auth challenges, not outcomes: the SDK resends the INVITE with credentials.
      if (status === 401 || status === 407) return;
      sipModeRef.current = true;
      if (status === 180 || status === 183) {
        clearRingingTimeoutRef.current();
        if (!connectedAtRef.current) setCallState('ringing');
      } else if (status >= 200 && status < 300) handleAnsweredSignalRef.current();
      else if ([486, 600, 603].includes(status)) finalizeCallRef.current('callRejected');
      else if ([408, 480, 487].includes(status)) finalizeCallRef.current('callMissed');
      else if (status >= 400) finalizeCallRef.current('callFailed');
    });
  }, [isSupportedPlatform]);

  const setRightPaneCollapsed = useAppStore((state) => state.setRightPaneCollapsed);

  const instanceRef = useRef<InnoCallsRTCInstance | null>(null);
  const mountKeyRef = useRef<string>('');
  // The business's own connected InnoCalls number (e.g. their Saudi number) —
  // used by formatDialTarget to decide whether a destination needs local
  // (same-country) or full E.164 (cross-country) formatting. Fetched once
  // alongside agent credentials during init, not re-fetched per call.
  const businessCallerNumberRef = useRef<string | undefined>(undefined);
  const activeCallRef = useRef<ActiveCall | null>(null);
  const connectedAtRef = useRef<number | null>(null);
  // InnoCalls' OWN call id — distinct from activeCallRef.current.providerCallId,
  // which is a client-generated correlation UUID InnoCalls has never seen (see
  // ActiveCall's doc comment / phone-call.entity.ts). This is the ONLY id
  // InnoCalls' recording-download endpoint accepts (GET /cdr/download-record/:callId
  // — https://innocalls.com/en/docs/webrtc/call-record, which explicitly states
  // this id "obtained from callStarted event" and has no server-side lookup
  // alternative — their call-campaign/single-call/list endpoint is for a
  // separate automated-dialing product and never contains interactive WebRTC
  // calls, confirmed by it always returning zero rows for real calls). Captured
  // opportunistically from every call-lifecycle event payload since the docs'
  // claim that it only appears once "answered" hasn't been verified against
  // this SDK build's actual (already-known-to-diverge-from-docs) payload shape.
  const innoCallsCallIdRef = useRef<string | null>(null);
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ringingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // How many ringing-shaped events (onCallRinging/onCallStarted) this call
  // has seen — reset to 0 at the start of every startCall(). See
  // handleRingingSignal's doc comment: the 2nd+ firing is treated as the
  // real answered signal on this SDK build.
  const ringingSignalCountRef = useRef(0);
  // True from the moment a call is finalized until the next startCall() —
  // guards against the SDK firing its own (now-stale) events AFTER we've
  // already finalized via the safety timeout, which would otherwise
  // re-trigger finalizeCall and appear to "restart" a call that's already
  // shown as finished.
  const finalizedRef = useRef(false);
  // Set inside the init effect once handleAnsweredSignal actually exists —
  // armRingingTimeout (declared before that effect runs) needs to call it
  // without depending on the effect's closure directly.
  const handleAnsweredSignalRef = useRef<() => void>(() => {});
  // True once this call's real SIP signalling has been seen (see subscribeSipSignals): from then
  // on answered / busy / no-answer come from SIP responses only — no ringing-count or 45 s guess.
  const sipModeRef = useRef(false);
  const finalizeCallRef = useRef<(reason: PhoneDialerEndReason) => unknown>(() => undefined);
  const clearRingingTimeoutRef = useRef<() => void>(() => {});

  const clearRingingTimeout = useCallback(() => {
    if (ringingTimeoutRef.current) {
      clearTimeout(ringingTimeoutRef.current);
      ringingTimeoutRef.current = null;
    }
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  }, []);

  const startDurationTimer = useCallback(() => {
    stopDurationTimer();
    durationTimerRef.current = setInterval(() => {
      if (!connectedAtRef.current) return;
      setDuration(Math.floor((Date.now() - connectedAtRef.current) / 1000));
    }, 1000);
  }, [stopDurationTimer]);

  // Pulls InnoCalls' own call id out of a call-lifecycle event payload,
  // whatever shape it turns out to be in — checked on every event (not just
  // callStarted) since this SDK build has already been shown to diverge from
  // InnoCalls' documented event shapes elsewhere in this file. Once found,
  // never overwritten for the rest of this call (first non-empty value wins).
  //
  // Confirmed via real browser console output: this SDK's .on('callStarted', ...)
  // payload is NOT the documented { call: { callId, status, ... } } object —
  // it's a bare string, e.g. "1786799943.99649-999-+201023356631-outbound"
  // (epoch-seconds.microseconds-something-phoneNumber-direction). Every other
  // event observed (callRinging, callEnded via both onCallX and .on()) fired
  // with `undefined`, carrying no data at all. So the string form is the only
  // known-real shape on this build — the object-shaped candidates are kept as
  // a fallback in case a future SDK version or a different event does send one.
  const captureInnoCallsCallId = (payload: any) => {
    if (innoCallsCallIdRef.current || payload == null) return;
    if (typeof payload === 'string' && payload.trim()) {
      innoCallsCallIdRef.current = payload.trim();
      console.log('[usePhoneDialer] Captured InnoCalls callId (string payload):', innoCallsCallIdRef.current);
      return;
    }
    const candidate =
      payload?.call?.callId ??
      payload?.callId ??
      payload?.call?.id ??
      payload?.id ??
      payload?.data?.callId ??
      payload?.data?.call?.callId;
    if (candidate != null && candidate !== '') {
      innoCallsCallIdRef.current = String(candidate);
      console.log('[usePhoneDialer] Captured InnoCalls callId:', innoCallsCallIdRef.current);
    }
  };

  const finalizeCall = useCallback(async (reason: PhoneDialerEndReason) => {
    if (finalizedRef.current) {
      console.log('[usePhoneDialer] Ignoring stale SDK event — call already finalized:', reason);
      return;
    }
    finalizedRef.current = true;

    const call = activeCallRef.current;
    stopDurationTimer();
    clearRingingTimeout();

    // Every finalize path except a genuine onCallEnded means OUR side is
    // deciding the call is over (safety timeout, rejected, missed, failed,
    // or the agent pressing "End Call") — the InnoCalls SDK doesn't know
    // that yet and will keep ringing/running in the background, later
    // firing its own real events on a call we've already shown as finished
    // ("keeps continuing after finished"). Force a real hangup on the SDK
    // for every non-callEnded reason so its state actually matches ours.
    if (reason !== 'callEnded') {
      try {
        instanceRef.current?.hangup?.();
      } catch (err) {
        console.warn('[usePhoneDialer] hangup during finalizeCall failed:', err);
      }
    }

    // Release the REAL hardware mic capture — confirmed as a real
    // production issue: InnoCalls' SDK only ever receives our synthetic,
    // denoised stream (see noise-suppression.service.web.ts), never the
    // actual raw getUserMedia stream, so it can never stop the real
    // capture itself. Without this, the browser's mic-in-use indicator
    // stayed lit indefinitely after a call ended. finalizeCall is the
    // single choke point every call-end path (SDK event or our own forced
    // hangup above) already funnels through, so this fires exactly once
    // per call regardless of how it ended.
    releaseActiveMicStreams();

    // Committed directly to InnoCalls' own SDK outcomes where possible, but
    // onCallEnded alone is NOT sufficient evidence of a genuine completed
    // call — confirmed via a real call record where the callee declined
    // within ~9s (duration 0, connectedAtRef never set) and the SDK fired
    // onCallEnded rather than onCallRejected. This build's onCallEnded
    // appears to be a generic "session over" event covering rejections too,
    // not exclusively a normal-completion signal. So: only treat callEnded
    // as 'completed' if the call actually reached a genuine answered state
    // (connectedAtRef set via handleAnsweredSignal); otherwise treat it the
    // same as a rejection ('cancelled') since it ended without ever being
    // answered. The SAME rule applies to userHangup — whichever party ends
    // an already-answered call, that's a completed call, not merely "ended";
    // 'ended' is reserved for the agent hanging up before the call was ever
    // answered (confirmed bug: an agent-ended call that WAS answered was
    // previously always saved as 'ended' regardless of wasAnswered, while
    // the callee ending the same call correctly saved 'completed' — the two
    // parties ending an answered call must produce the same outcome).
    //  - callEnded (was answered)     -> 'completed'
    //  - callEnded (never answered)   -> 'cancelled' (declined/hung up unanswered)
    //  - callMissed                   -> 'not-responded' (callee never picked up)
    //  - callRejected                 -> 'cancelled' (callee busy/declined)
    //  - userHangup (was answered)    -> 'completed'
    //  - userHangup (never answered)  -> 'ended' (agent hung up before InnoCalls
    //                                     reported any of the outcomes above)
    //  - callFailed                   -> 'failed' (technical failure, not a call outcome)
    const wasAnswered = !!connectedAtRef.current;
    const terminalState: PhoneDialerCallState =
      reason === 'callEnded' ? (wasAnswered ? 'completed' : 'cancelled') :
      reason === 'callMissed' ? 'not-responded' :
      reason === 'callRejected' ? 'cancelled' :
      reason === 'userHangup' ? (wasAnswered ? 'completed' : 'ended') :
      'failed';
    setCallState(terminalState);

    if (call) {
      // Duration measured from the genuine answered signal (connectedAtRef,
      // set only in handleAnsweredSignal) to the terminal event — 0 for any
      // call that never actually connected, matching terminalState above.
      const duration = connectedAtRef.current ? Math.floor((Date.now() - connectedAtRef.current) / 1000) : 0;
      try {
        await apiService.updatePhoneCall(call.providerCallId, {
          status: terminalState,
          duration,
          endedAt: new Date().toISOString(),
          // When the call was actually answered (powers ring-time reports)
          ...(connectedAtRef.current ? { answeredAt: new Date(connectedAtRef.current).toISOString() } : {}),
          // Captured from the WebRTC SDK's own event payloads — the ONLY
          // source InnoCalls' recording-download endpoint accepts (see
          // captureInnoCallsCallId's doc comment). Omitted when never
          // captured (e.g. a call that never answered has no callId per
          // InnoCalls' docs) rather than sending an empty/null value.
          ...(innoCallsCallIdRef.current ? { providerMetadata: { innoCallsCallId: innoCallsCallIdRef.current } } : {}),
        });
      } catch (err) {
        console.warn('[usePhoneDialer] Failed to update call record on end:', err);
      }
    }

    activeCallRef.current = null;
    connectedAtRef.current = null;
    // Every terminal state — including 'completed'/'ended' — is left on
    // screen, not auto-cleared. A completed call still has actionable
    // post-call quick actions (add a reminder, add to a list, change
    // assignment) that need the screen to stay put until the user is done
    // with them, not vanish after a fixed delay. dismissCall() (wired to the
    // screen's own "Dismiss"/close control) is the only way back to idle now.
  }, [stopDurationTimer, clearRingingTimeout]);

  finalizeCallRef.current = finalizeCall;
  clearRingingTimeoutRef.current = clearRingingTimeout;

  const dismissCall = useCallback(() => {
    setCallState('idle');
    setDuration(0);
    setContact(null);
    setMinimized(false);
    setError(null);
    setDiagnostics([]);
  }, []);

  // Safety net for a call that starts ringing (inbound OR outbound) and
  // never resolves — e.g. the SDK's onCallMissed/onCallEnded never fires
  // (confirmed unreliable in testing) or the receptionist simply never
  // answers/declines. Without this, "ringing" persisted indefinitely with
  // no way to reach a terminal state.
  //
  // CRITICAL: this must NOT hang up a call that was actually answered. This
  // SDK build has no confirmed, reliable "answered" event (onCallAnswered
  // doesn't exist as a property; the speculative .on('callAnswered', ...)
  // listener below has never been observed to fire) — so a real, live,
  // in-progress conversation looks IDENTICAL to us as "still ringing" the
  // whole time. The previous version of this timeout unconditionally called
  // finalizeCall('callMissed') at 30s, which was hanging up genuinely
  // answered calls mid-conversation (confirmed: audio flowing, both parties
  // talking, then the call died and showed as failed/missed around the
  // 30-60s mark). Real no-answer calls resolve via a DIFFERENT SDK event
  // (onCallMissed/onCallRejected/onCallEnded) well before this fires in
  // practice, so treat this long a ring as itself strong evidence the call
  // WAS answered — promote to 'connected' instead of killing it. Re-armed
  // every time a fresh ringing-shaped event fires (a new call); cleared
  // whenever the call leaves ringing for any resolved state.
  const armRingingTimeout = useCallback(() => {
    clearRingingTimeout();
    // Real signalling available: ringing ends with a SIP answer or a final response, never a guess.
    if (sipModeRef.current) return;
    ringingTimeoutRef.current = setTimeout(() => {
      console.warn('[usePhoneDialer] No answered/ended event within the ringing window — assuming the call was answered (this SDK has no reliable answered signal) rather than hanging up a possibly-live call');
      handleAnsweredSignalRef.current?.();
    }, RINGING_ASSUME_ANSWERED_MS);
  }, [clearRingingTimeout]);

  useEffect(() => {
    if (!isSupportedPlatform) return;
    // Confirmed real bug: without this, the very first call below
    // (getPhoneAgentCredentials, an authenticated request) fires
    // immediately on mount and races CallScreen.tsx's own async token
    // redemption — losing that race throws on the missing auth header,
    // and nothing ever retried since this effect's own deps never changed
    // again. Skipping the attempt entirely while not yet authenticated
    // (rather than attempting and permanently failing) lets this effect
    // correctly re-run once authReady flips true (see the dependency
    // array below) instead of being stuck forever.
    if (!authReady) return;

    let cancelled = false;

    const init = async () => {
      try {
        console.log('[usePhoneDialer] Fetching agent credentials...');
        const [credsRes, accountRes] = await Promise.all([
          apiService.getPhoneAgentCredentials(),
          apiService.getPhoneAccount().catch(() => null),
        ]);
        businessCallerNumberRef.current = accountRes?.data?.callerNumber || undefined;
        console.log('[usePhoneDialer] Business caller number:', businessCallerNumberRef.current || '(none configured)');
        const creds = credsRes?.data;
        console.log('[usePhoneDialer] Credentials response:', {
          hasApiKey: !!creds?.apiKey,
          hasExtension: !!creds?.extension,
          hasWebrtcSecret: !!creds?.webrtcSecret,
          statusCode: credsRes?.statusCode,
        });
        if (!creds?.apiKey || !creds?.extension || !creds?.webrtcSecret) {
          console.warn('[usePhoneDialer] Missing credentials — dialer will not initialize. Is the phone channel connected and is this user provisioned as an agent?');
          setIsReady(false);
          return;
        }

        console.log('[usePhoneDialer] Loading InnoCalls WebRTC script...');
        ensureInnocallsStylesheet();
        await ensureInnocallsScript();
        if (cancelled) return;
        console.log('[usePhoneDialer] Script loaded, waiting for window.InnocallsRTC...');

        const InnocallsRTC = await waitForInnocallsRTC();
        if (cancelled) return;
        console.log('[usePhoneDialer] window.InnocallsRTC is available');

        const mountKey = `${creds.apiKey}:${creds.extension}:${creds.webrtcSecret}`;
        if (instanceRef.current && mountKeyRef.current === mountKey) {
          console.log('[usePhoneDialer] Reusing existing dialer instance');
          setIsReady(true);
          return;
        }

        destroyInnoCallsInstance(instanceRef.current, DIALER_ROOT_ID);

        const dialerRoot = document.getElementById(DIALER_ROOT_ID);
        console.log('[usePhoneDialer] Dialer root element present in DOM:', !!dialerRoot);

        console.log('[usePhoneDialer] Constructing InnocallsRTC instance with extension:', creds.extension);
        // Matches the confirmed-working reference implementation's
        // constructor call exactly (including passing `config`, which we'd
        // previously omitted) — a proven InnoCalls integration in another
        // project successfully places calls with this exact shape.
        const instance = new InnocallsRTC({
          apiKey: creds.apiKey,
          extension: creds.extension,
          webrtcSecret: creds.webrtcSecret,
          config: {
            baseColor: '#0EA5E9',
          },
          terminateOnRefresh: true,
        });

        // Confirmed via runtime inspection (console.dir of a real instance):
        // the SDK does NOT expose .on()/.off() — it has pre-existing onCallX
        // properties that must be overwritten directly.
        //
        // IMPORTANT: onCallStarted fires when the call starts RINGING on the
        // callee's phone, not when they answer — a real successful test call
        // showed onCallStarted firing immediately after dial, well before any
        // human could have answered. There is no onCallAnswered property on
        // this SDK build, so "truly connected" has no dedicated event; we
        // treat onCallStarted as the ringing signal (matching its real
        // firing behavior) and move past 'ringing' either when a later
        // onCallAnswered-shaped signal arrives via .on() below, OR — since
        // that listener has never been observed to actually fire on this SDK
        // build — once armRingingTimeout's window elapses without any
        // terminal event (missed/rejected/ended), at which point we assume
        // the call WAS answered rather than leaving it stuck on "Ringing…"
        // indefinitely and, worse, killing a genuinely live call (see
        // armRingingTimeout's doc comment for the incident this fixed).
        // Duration must NOT start here — ringing is not "answered", and
        // starting the clock on ringing over-counts calls that are hung up
        // before the callee ever picks up (e.g. an 8s call that was never
        // answered showing an 8s duration). The clock only starts once
        // handleAnsweredSignal actually runs (real event or the timeout
        // fallback above).
        const handleAnsweredSignal = () => {
          clearRingingTimeout();
          if (!connectedAtRef.current) {
            connectedAtRef.current = Date.now();
            startDurationTimer();
          }
          setCallState('connected');
        };
        handleAnsweredSignalRef.current = handleAnsweredSignal;

        // Confirmed in production: handleRingingSignal firing a SECOND time
        // (i.e. onCallStarted/onCallRinging firing again after the first)
        // lines up exactly with the callee actually picking up — the first
        // firing is the "starts ringing on their phone" signal (confirmed
        // earlier, fires immediately on dial), and this SDK build appears to
        // reuse the same event to also signal the media session actually
        // establishing, rather than exposing a distinct onCallAnswered.
        // ringingSignalCountRef tracks how many times this call has seen a
        // ringing-shaped event; the 2nd+ one is treated as answered instead
        // of restarting ringing — previously this had NO guard at all and
        // would stomp an already-'connected' call straight back to
        // 'ringing' on this second firing, which is the bug just observed.
        const handleRingingSignal = () => {
          finalizedRef.current = false;
          ringingSignalCountRef.current += 1;
          if (sipModeRef.current) {
            // SIP decides "answered" (200 OK); a repeated SDK event is not an answer.
            clearRingingTimeout();
            if (!connectedAtRef.current) setCallState('ringing');
            return;
          }
          if (ringingSignalCountRef.current >= 2 || connectedAtRef.current) {
            handleAnsweredSignal();
            return;
          }
          armRingingTimeout();
          setCallState('ringing');
        };

        instance.onCallRinging = (p) => { console.log('[usePhoneDialer] event: onCallRinging', p); captureInnoCallsCallId(p); handleRingingSignal(); };
        instance.onCallStarted = (p) => { console.log('[usePhoneDialer] event: onCallStarted', p); captureInnoCallsCallId(p); handleRingingSignal(); };
        instance.onCallEnded = (p) => { console.log('[usePhoneDialer] event: onCallEnded', p); captureInnoCallsCallId(p); finalizeCall('callEnded'); };
        instance.onCallMissed = (p) => { console.log('[usePhoneDialer] event: onCallMissed', p); finalizeCall('callMissed'); };
        instance.onCallRejected = (p) => { console.log('[usePhoneDialer] event: onCallRejected', p); finalizeCall('callRejected'); };

        // Also try .on() in case a different code path exposes it — cheap
        // and harmless if unsupported (the ?. no-ops).
        instance.on?.('callRinging', (p) => { console.log('[usePhoneDialer] event(on): callRinging', p); captureInnoCallsCallId(p); handleRingingSignal(); });
        instance.on?.('callStarted', (p) => { console.log('[usePhoneDialer] event(on): callStarted', p); captureInnoCallsCallId(p); handleRingingSignal(); });
        instance.on?.('callAnswered', (p) => { console.log('[usePhoneDialer] event(on): callAnswered', p); captureInnoCallsCallId(p); handleAnsweredSignal(); });
        instance.on?.('callEnded', (p) => { console.log('[usePhoneDialer] event(on): callEnded', p); captureInnoCallsCallId(p); finalizeCall('callEnded'); });
        instance.on?.('callMissed', (p) => { console.log('[usePhoneDialer] event(on): callMissed', p); finalizeCall('callMissed'); });
        instance.on?.('callRejected', (p) => { console.log('[usePhoneDialer] event(on): callRejected', p); finalizeCall('callRejected'); });
        instance.on?.('callFailed', (p) => { console.log('[usePhoneDialer] event(on): callFailed', p); finalizeCall('callFailed'); });

        console.log('[usePhoneDialer] instance.mount exists:', typeof instance.mount === 'function');
        instance.mount?.(`#${DIALER_ROOT_ID}`);
        instanceRef.current = instance;
        mountKeyRef.current = mountKey;

        // Confirmed real bug: the SDK reports "ready" the instant its JS
        // constructor returns, but that's ONLY proof the object exists —
        // not that its actual SIP/WebSocket registration with InnoCalls'
        // signaling server has completed. The shipped SDK build exposes no
        // real event-emitter (confirmed: no registered/connected callback
        // anywhere in its onCallX/`.on()` surface, see the wiring above),
        // so there is no genuine signal to wait for. On a cold WebView
        // (fresh page, no reused DNS/TLS/WS session), placing a call before
        // that handshake finishes has the SDK silently accept startCall()
        // and never actually place it — exactly the "first call after
        // opening the screen always fails" pattern this was built to fix.
        // This fixed buffer is a deliberate, visible heuristic (not a
        // guaranteed fix) — the caller's own loading UI stays up for
        // however long isReady takes to flip true, so the agent sees a
        // "connecting" state rather than either a silent hidden delay or a
        // call that's placed too early and fails.
        const REGISTRATION_SETTLE_MS = 1000;
        await new Promise((resolve) => setTimeout(resolve, REGISTRATION_SETTLE_MS));
        if (cancelled) return;

        setIsReady(true);
        console.log('[usePhoneDialer] Dialer instance mounted and ready');

        // TEMPORARY DEBUG AID — exposes the raw SDK instance on window so it
        // can be inspected/poked directly from the browser console (e.g.
        // `window.__innocallsInstance`) while diagnosing why startCall()
        // makes no network call and fires no events. Remove once resolved.
        (window as any).__innocallsInstance = instance;
        console.log('[usePhoneDialer] DEBUG: instance exposed as window.__innocallsInstance — inspect its properties/methods directly, e.g. console.dir(window.__innocallsInstance)');
      } catch (err: any) {
        console.error('[usePhoneDialer] Failed to initialize dialer:', err);
        setError(err?.message || 'Failed to initialize phone dialer');
        setIsReady(false);
      }
    };

    void init();

    return () => {
      cancelled = true;
    };
  }, [isSupportedPlatform, authReady, finalizeCall, startDurationTimer, clearRingingTimeout, armRingingTimeout]);

  useEffect(() => () => {
    stopDurationTimer();
    destroyInnoCallsInstance(instanceRef.current, DIALER_ROOT_ID);
    instanceRef.current = null;
    teardownNoiseSuppression();
  }, [stopDurationTimer]);

  const startCall = useCallback(async (phoneNumber: string, contactId?: string, displayInfo?: PhoneCallContactInfo) => {
    console.log('[usePhoneDialer] startCall requested', { phoneNumber, contactId, hasInstance: !!instanceRef.current, isReady });

    if (!isSupportedPlatform) {
      setError('Calling is only available on the web app for now.');
      return;
    }
    if (!instanceRef.current || !isReady) {
      console.warn('[usePhoneDialer] startCall aborted — dialer instance not ready', { hasInstance: !!instanceRef.current, isReady });
      setError('Phone dialer is not ready yet.');
      return;
    }

    finalizedRef.current = false;
    ringingSignalCountRef.current = 0;
    sipModeRef.current = false;
    innoCallsCallIdRef.current = null;
    setDiagnostics([]);
    // sanitized (full E.164) is what we STORE — call records/history must
    // always show the real international number regardless of destination.
    // dialTarget is what we actually SEND to InnoCalls' SDK — local format
    // (leading "0", no country code) when the destination matches the
    // business's own configured country, full E.164 otherwise. See
    // formatDialTarget's doc comment for why: InnoCalls silently drops
    // same-country calls sent in E.164 (no SIP response at all), confirmed
    // via a real Saudi-business-number account failing to reach a Saudi
    // destination while an Egyptian destination worked fine.
    const sanitized = normalizePhoneNumber(phoneNumber);
    const dialTarget = formatDialTarget(phoneNumber, businessCallerNumberRef.current);
    console.log('[usePhoneDialer] Normalized phone number:', { input: phoneNumber, sanitized, dialTarget, businessCallerNumber: businessCallerNumberRef.current });
    if (!sanitized) {
      setError('Invalid phone number.');
      return;
    }

    setError(null);
    setContact({ ...(displayInfo ?? { name: phoneNumber }), contactId, phoneNumber, dialTarget });
    setMinimized(false);
    // The in-call screen is hosted inside the right pane — force it open
    // (and un-collapsed) so the call is visible regardless of which screen
    // or pane state the user was in when they placed the call.
    setRightPaneCollapsed(false);

    // InnoCalls' WebRTC SDK needs microphone access to place a call — the
    // reference implementation requests this explicitly before dialing.
    // Without it, the SDK appears to silently no-op (accepts startCall()
    // without throwing, but never fires any event and makes no network call).
    if (navigator.mediaDevices?.getUserMedia) {
      try {
        console.log('[usePhoneDialer] Requesting microphone permission...');
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('[usePhoneDialer] Microphone permission granted');
        stream.getTracks().forEach((track) => track.stop());
      } catch (micErr: any) {
        console.error('[usePhoneDialer] Microphone permission denied or unavailable:', micErr);
        setError('Microphone access is required to place calls.');
        return;
      }
    } else {
      console.warn('[usePhoneDialer] navigator.mediaDevices.getUserMedia is not available in this environment');
    }
    const providerCallId = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    try {
      await apiService.createPhoneCall({
        phoneNumber: sanitized,
        contactId,
        direction: 'outbound',
        providerCallId,
      });
      console.log('[usePhoneDialer] Call record created:', providerCallId);
    } catch (err) {
      console.warn('[usePhoneDialer] Failed to create call record (continuing anyway):', err);
    }

    activeCallRef.current = { providerCallId, phoneNumber: sanitized, contactId, startedAt: Date.now() };

    console.log('[usePhoneDialer] Instance methods available:', {
      hasShowWebrtc: typeof instanceRef.current.showWebrtc === 'function',
      hasFillNumber: typeof instanceRef.current.fillNumber === 'function',
      hasStartCall: typeof instanceRef.current.startCall === 'function',
      hasOn: typeof instanceRef.current.on === 'function',
    });

    try {
      instanceRef.current.showWebrtc?.();
      instanceRef.current.fillNumber?.(dialTarget);
      console.log('[usePhoneDialer] Calling instance.startCall(', dialTarget, ')');
      instanceRef.current.startCall?.(dialTarget);
      console.log('[usePhoneDialer] instance.startCall() returned without throwing — waiting for callRinging/callStarted event');
      // Our own optimistic state before InnoCalls confirms anything — becomes
      // 'ringing' once its own onCallRinging event actually fires.
      setCallState('calling');

      // The SDK may accept startCall() without ever firing a real event back
      // (confirmed happening in testing) — without this timeout, the UI
      // would show "Calling…" forever with no feedback. 15s is a generous
      // window for a ringing signal to arrive.
      clearRingingTimeout();
      ringingTimeoutRef.current = setTimeout(() => {
        console.warn('[usePhoneDialer] No callRinging/callStarted event received within 15s — treating as failed');
        setError('The call could not be connected. Please try again.');
        finalizeCall('callFailed');
      }, 15000);

      // NOTE: an earlier version of this code tried to detect "answered"
      // early via a patched RTCPeerConnection reaching 'connected' — REMOVED.
      // Confirmed in production: it fired as soon as ringing began (ICE/
      // signaling setup happens well before the callee ever picks up), not
      // on a real answer — which made the duration counter start immediately
      // AND caused calls the callee actually declined/never answered to be
      // misreported as 'completed' instead of 'cancelled'/'not-responded'.
      // There is no dependable early-answered signal from this SDK build —
      // 'connected' is now driven ONLY by a genuine SDK event (if one ever
      // fires) or armRingingTimeout's fixed-delay last-resort fallback below,
      // never by a heuristic. This means the counter may start later than
      // the true answer moment, but it will never fire before the call is
      // genuinely live, and correct terminal outcomes (busy/no-answer/
      // rejected) are never misclassified as completed because of it.
    } catch (err: any) {
      console.error('[usePhoneDialer] startCall threw:', err);
      setError(err?.message || 'Failed to start call');
      finalizeCall('callFailed');
    }
  }, [isSupportedPlatform, isReady, finalizeCall, clearRingingTimeout, setRightPaneCollapsed]);

  const hangup = useCallback(() => {
    console.log('[usePhoneDialer] hangup requested by agent', { hasInstance: !!instanceRef.current, hasHangupMethod: typeof instanceRef.current?.hangup === 'function' });
    // The agent is ending the call themselves, before InnoCalls has reported
    // any of its own outcomes (callEnded/callMissed/callRejected) — finalize
    // immediately as 'userHangup' rather than waiting for the SDK to catch
    // up; finalizeCall's own hangup() call (for non-callEnded reasons)
    // handles actually terminating the SDK session.
    return finalizeCall('userHangup');
  }, [finalizeCall]);

  return {
    isSupportedPlatform,
    isReady,
    callState,
    duration,
    error,
    contact,
    minimized,
    setMinimized,
    startCall,
    hangup,
    dismissCall,
    dialerRootId: DIALER_ROOT_ID,
    diagnostics,
  };
}
